import { Ref, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { IdType, Row, RowPropGetter, TableInstance, useFilters, useSortBy, useTable } from 'react-table';
import { TableColumn, TableRowDataType, TooltipTitleType } from './types';
import { SortIcon } from './SortIcon';
import { joinClassNames } from '../../utils';
import { sortRowsBySecondary } from './utils';
import styles from './styles.module.scss';

export type TableProps<TableRowData extends TableRowDataType> = {
  /**
   * Custom properties: { fitToContent?: boolean }
   */
  columns: TableColumn<TableRowData>[];
  data: TableRowData[];
  initHiddenColumns?: IdType<TableRowData>[];
  initSortColumnId?: IdType<TableRowData>;
  isInitSortDesc?: boolean;
  sortSecondaryColumnId?: IdType<TableRowData>;
  /**
   * CSS max-height value
   */
  maxHeight?: string;
  /**
   * Disable automatic sorting (use this when sorting in the backend)
   */
  isManualSort?: boolean;
  onSort?: (currentSortId: string, isSortedDesc: boolean) => void;
  onRowClick?: (rowData: TableRowData) => void;
  tableRef?: Ref<TableInstance<TableRowData>>;
  rowProps?: (row: Row<TableRowData>) => RowPropGetter<TableRowData>;
};

const getTooltipTitleProps = (
  tooltipTitle?: TooltipTitleType | string
): { title: string | undefined } | undefined => {
  switch (tooltipTitle) {
    case TooltipTitleType.None:
      return { title: undefined };
    case TooltipTitleType.Default:
      return undefined;
  }
  return tooltipTitle ? { title: tooltipTitle } : undefined;
};

type TableRowProps<TableRowData extends TableRowDataType> = {
  row: Row<TableRowData>;
  prepareRow: (row: Row<TableRowData>) => void;
  onRowClick?: (original: TableRowData) => void;
  rowProps?: (row: Row<TableRowData>) => RowPropGetter<TableRowData>;
};
const TableRow = <TableRowData extends TableRowDataType>({
  row,
  prepareRow,
  onRowClick,
  rowProps = () => ({}),
}: TableRowProps<TableRowData>) => {
  return useMemo(() => {
    prepareRow(row);
    const { getRowProps, cells, original } = row;
    const handleRowClick = onRowClick ? () => onRowClick(original) : undefined;
    const { key: rowKey, ...restRowProps } = getRowProps(rowProps(row));

    return (
      <tr
        key={rowKey}
        className={joinClassNames(styles.tbodyTr, onRowClick && styles.trClickable)}
        onClick={handleRowClick}
        {...restRowProps}
      >
        {cells.map(({ getCellProps, render }) => {
          const { key: cellKey, ...restCellProps } = getCellProps();
          return (
            <td key={cellKey} className={styles.td} {...restCellProps}>
              {render('Cell')}
            </td>
          );
        })}
      </tr>
    );
  }, [onRowClick, prepareRow, row, rowProps]);
};

export function Table<TableRowData extends TableRowDataType>({
  columns,
  data,
  initHiddenColumns = [],
  initSortColumnId,
  isInitSortDesc = false,
  sortSecondaryColumnId,
  maxHeight,
  isManualSort = false,
  onSort,
  onRowClick,
  tableRef,
  rowProps,
}: TableProps<TableRowData>) {
  const tableColumns = useMemo(() => columns, [columns]);
  const tableData = useMemo(() => data ?? [], [data]);

  const tableInstance = useTable<TableRowData>(
    {
      columns: tableColumns,
      data: tableData,
      disableMultiSort: true,
      disableSortRemove: true,
      autoResetSortBy: false,
      initialState: {
        sortBy: initSortColumnId
          ? [
              {
                id: initSortColumnId,
                desc: isInitSortDesc,
              },
            ]
          : [],
        hiddenColumns: initHiddenColumns,
      },
      manualSortBy: isManualSort,
    },
    useFilters,
    useSortBy
  );

  useImperativeHandle(tableRef, () => tableInstance);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = tableInstance;

  const isFirstUpdate = useRef(true);
  useEffect(() => {
    if (isFirstUpdate.current) {
      isFirstUpdate.current = false;
      return;
    }

    if (onSort !== undefined) {
      const currentSortId = sortBy[0]?.id;
      const isSortedDesc = sortBy[0]?.desc || false;
      onSort(currentSortId, isSortedDesc);
    }
  }, [onSort, sortBy]);

  const currentSortId = sortBy[0]?.id;

  const sortedRows =
    !isManualSort && sortSecondaryColumnId && currentSortId !== sortSecondaryColumnId
      ? sortRowsBySecondary(rows, currentSortId, sortSecondaryColumnId)
      : rows;

  return (
    <div
      className={styles.tableContainer}
      style={
        maxHeight
          ? {
              maxHeight,
              overflow: 'auto',
            }
          : undefined
      }
    >
      <table className={styles.table} {...getTableProps()}>
        <thead className={styles.thead}>
          {headerGroups.map(({ getHeaderGroupProps, headers }) => {
            const { key: rowKey, ...restHeaderGroupProps } = getHeaderGroupProps();
            return (
              <tr key={rowKey} {...restHeaderGroupProps}>
                {headers.map(
                  ({
                    getHeaderProps,
                    getSortByToggleProps,
                    disableSortBy,
                    isSorted,
                    isSortedDesc,
                    render,
                    fitToContent,
                    tooltipTitle,
                  }) => {
                    const { key: cellKey, ...restHeaderProps } = getHeaderProps(
                      getSortByToggleProps(getTooltipTitleProps(tooltipTitle))
                    );
                    return (
                      <th
                        key={cellKey}
                        className={joinClassNames(styles.th, fitToContent && styles.fitToContent)}
                        {...restHeaderProps}
                      >
                        <span className={styles.thContent}>
                          {render('Header')}
                          {!disableSortBy && (
                            <SortIcon isSorted={isSorted} isSortedDesc={isSortedDesc || false} />
                          )}
                        </span>
                      </th>
                    );
                  }
                )}
              </tr>
            );
          })}
        </thead>

        <tbody {...getTableBodyProps()}>
          {sortedRows.map((row) => (
            <TableRow<TableRowData>
              row={row}
              onRowClick={onRowClick}
              prepareRow={prepareRow}
              key={row.id}
              rowProps={rowProps}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

Table.displayName = 'Table';
